import { Box, Flex, xcss } from '@atlaskit/primitives';
import { DatabaseFieldType } from '@databases/types/database';
import { UserCard } from '@databases/user';
import {
  MultiValueChangeType,
  type ValueChange,
} from '@databases/utils/database/changesets';
import React, { useCallback } from 'react';

import { FieldValueRemoveButton, filterOutRemovedValue } from '../popover-edit-common';

import { type ResolvedUser, type Value } from './types';

const closeButtonStyling = xcss({
  paddingLeft: 'space.050',
});

interface UserPopoverValueProps {
  user: ResolvedUser;
  resolvedUsers: ResolvedUser[];
  onChange: (value: Value[], change: ValueChange | null) => void;
  autoFocus: boolean | undefined;
  addDataSectionRef: React.MutableRefObject<HTMLDivElement | null>;
}

export const UserPopoverValue: React.FunctionComponent<UserPopoverValueProps> = ({
  user,
  resolvedUsers,
  onChange,
  autoFocus,
  addDataSectionRef,
}) => {
  const userValueIndex = resolvedUsers.findIndex(
    (value) =>
      value &&
      'accountId' in value &&
      'accountId' in user &&
      value.accountId === user.accountId &&
      'displayName' in value &&
      'displayName' in user &&
      value.displayName === user.displayName
  );
  const remainingValues = filterOutRemovedValue({
    currentValues: resolvedUsers,
    indexToRemove: userValueIndex,
  });

  const getUserDisplayName = (user: ResolvedUser): string | undefined => {
    if (user !== null && 'displayName' in user) {
      return user.displayName;
    }
    return '';
  };

  const getUserAccountId = (user: ResolvedUser): string => {
    if (user !== null && 'accountId' in user) {
      return user.accountId;
    }
    return '';
  };

  const onItemDelete = useCallback(() => {
    onChange(remainingValues, {
      type: DatabaseFieldType.USER,
      delta: {
        type: MultiValueChangeType.REMOVE,
        index: userValueIndex,
      },
    });

    // focus the select input after each item removal
    addDataSectionRef.current?.getElementsByTagName('input')[0].focus();
  }, [addDataSectionRef, onChange, userValueIndex, remainingValues]);

  return (
    <Flex
      testId="user-popover-value"
      justifyContent="start"
      alignItems="center"
      gap="space.075"
    >
      <UserCard
        accountId={getUserAccountId(user)}
        displayName={getUserDisplayName(user)}
        variant="avatar"
        size="xsmall"
      />
      <Box xcss={closeButtonStyling}>
        <FieldValueRemoveButton
          testId={'user-remove-button'}
          onItemDelete={onItemDelete}
          autoFocus={autoFocus}
        />
      </Box>
    </Flex>
  );
};
